import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useProposals } from "../hooks/useProposals";
import { api } from "../lib/api";
import { distanceKm } from "../lib/geo";
import { SiteFooter, SiteHeader } from "../components/SiteHeader";
import { ExploreMap } from "../components/LazyMaps";
import { ListCard, Tile } from "../components/ProposalCards";
import { Empty, Loader } from "../components/Bits";
import { AreaChips, SearchForm, SortSeg, TopicSelect } from "../components/Filters";
import { useTopics } from "../hooks/useTopics";
import MobileHome from "../components/home/MobileHome";

const STEPS = [
  ["1. Share your idea", "Pick a spot on the map, add a before and after, and tell the story of what you’d change."],
  ["2. We check it", "Our team reviews every proposal before it goes live, so the map stays useful and respectful."],
  ["3. People vote", "Anyone can like or dislike it, and the most liked ideas can be brought to Cork City Council."],
];

const Hero = () => (
  <section aria-labelledby="hero-h" style={{ position: "relative", minHeight: "max(680px, 47.2vw)", overflow: "hidden", background: "#f6eee8" }} data-testid="home-hero">
    <img src="/illustrations/cork-hero.svg" alt="Illustration of Cork city: colourful quayside houses along the River Lee, St Patrick’s Bridge and the Shandon steeple on the hill"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center bottom" }} />
    <div className="wrap" style={{ position: "relative", paddingTop: 160 }}>
      <div style={{ maxWidth: 640 }}>
        <h1 id="hero-h" className="head rise" style={{ fontWeight: 800, fontSize: "clamp(48px, 6vw, 76px)", lineHeight: 0.98, letterSpacing: "-.035em", margin: "0 0 20px", color: "var(--ink)" }}>
          Your city. <span style={{ color: "#C8102E" }}>Your ideas.</span>
        </h1>
        <p className="rise rise-1" style={{ fontSize: 23, lineHeight: 1.45, color: "#2f2f35", margin: "0 0 30px", maxWidth: 560 }}>
          Show how any corner of Cork could be better, and vote for the ideas you want to see happen.
        </p>
        <div className="rise rise-2" style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <Link to="/new" className="btn btn-primary btn-lg" data-testid="hero-start-proposal">Start a proposal</Link>
          <a href="#map" className="btn btn-outline btn-lg" data-testid="hero-explore-map">Explore the map<ArrowDown size={18} /></a>
        </div>
        <p className="rise rise-3" style={{ margin: "18px 0 0", fontSize: 15, color: "#4a4a52" }}>Free for everyone. Every proposal is checked before it goes live.</p>
      </div>
    </div>
  </section>
);

const TopThree = ({ items }) => (
  <section aria-labelledby="top-h" className="wrap" style={{ paddingTop: 96, paddingBottom: 24 }}>
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 16, marginBottom: 28 }}>
      <div>
        <p className="kicker">Most liked</p>
        <h2 id="top-h" className="head" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.025em", margin: 0 }}>The ideas Cork is behind right now</h2>
      </div>
      <Link to="/proposals" style={{ fontWeight: 600, fontSize: 16 }} data-testid="see-all-proposals-link">See all proposals</Link>
    </div>
    <ol style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24, padding: 0, margin: 0 }}>
      {items.map((p, i) => <Tile key={p.id} p={p} rank={i + 1} testId={`top-tile-${i + 1}`} />)}
    </ol>
  </section>
);

const HowItWorks = () => (
  <section id="how" aria-labelledby="how-h" className="wrap" style={{ paddingTop: 80, paddingBottom: 96 }}>
    <h2 id="how-h" className="head" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.025em", margin: "0 0 32px" }}>How it works</h2>
    <ol style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 24, padding: 0, margin: 0, listStyle: "none" }}>
      {STEPS.map(([h, t], i) => (
        <li key={h} className={`rise rise-${i + 1}`} style={{ background: i === 0 ? "var(--tint-2)" : "var(--soft)", borderRadius: 18, padding: 28, borderTop: "4px solid #C8102E" }}>
          <h3 className="head" style={{ fontSize: 24, margin: "0 0 10px" }}>{h}</h3>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: "var(--body)", margin: 0 }}>{t}</p>
        </li>
      ))}
    </ol>
  </section>
);

function useHomeState() {
  const [params, setParams] = useSearchParams();
  const area = params.get("area") || "";
  const topic = params.get("topic") || "";
  const focus = params.get("focus");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState("");
  const { items, loading } = useProposals({ sort, area, q, topic });
  const topicName = useTopics().find((t) => t.id === topic)?.name || "";
  const [selected, setSelected] = useState(null);
  const [fly, setFly] = useState(null);
  const [me, setMe] = useState(null);
  const setParam = (key) => (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(key, v);
    else next.delete(key);
    next.delete("focus");
    setParams(next, { replace: true });
  };
  const setArea = setParam("area");
  const setTopic = setParam("topic");
  const clear = () => {
    setQ("");
    setDraft("");
    setParams(new URLSearchParams(), { replace: true });
  };
  const show = (p) => {
    setSelected(p.id);
    setFly({ lng: p.lng, lat: p.lat, zoom: 15.2 });
  };
  const nearMe = (pos, p) => {
    setMe(pos);
    setSelected(null);
    if (!p) setFly({ lng: pos.lng, lat: pos.lat, zoom: 15 });
    else if (distanceKm(pos, p) > 25) setFly({ lng: p.lng, lat: p.lat, zoom: 15.2, select: p.id });
    else setFly({ bounds: [[Math.min(pos.lng, p.lng), Math.min(pos.lat, p.lat)], [Math.max(pos.lng, p.lng), Math.max(pos.lat, p.lat)]], select: p.id });
  };
  useEffect(() => {
    const p = focus && items.find((x) => x.id === focus);
    if (p) {
      setSelected(p.id);
      setFly({ lng: p.lng, lat: p.lat, zoom: 15.2 });
    }
  }, [focus, items]);
  return { area, setArea, topic, setTopic, topicName, clear, sort, setSort, q, setQ, draft, setDraft, items, loading, selected, setSelected, fly, show, focus, me, nearMe };
}

const useTopThree = () => {
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/proposals", { params: { sort: "liked" } }).then(({ data }) => setTop(data.proposals.slice(0, 3)));
    document.title = "ReimagineCork: Your city. Your ideas.";
  }, []);
  return top;
};

const ExploreSection = ({ s, mapBox }) => (
  <>
    <section id="map" aria-labelledby="map-h" className="wrap" style={{ scrollMarginTop: 84, paddingTop: 64, paddingBottom: 24, display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "20px 48px" }}>
      <div style={{ flex: "1 1 420px", minWidth: 0 }}>
        <h2 id="map-h" className="head" style={{ fontWeight: 800, fontSize: 40, letterSpacing: "-.025em", margin: "0 0 8px" }}>Explore ideas across Cork</h2>
        <p style={{ fontSize: 18, color: "var(--body)", margin: 0 }}>Tap a pin to see what people are proposing, then like the ones you want to see happen.</p>
      </div>
      <SearchForm value={s.draft} onChange={s.setDraft} onSubmit={() => s.setQ(s.draft.trim())} />
    </section>
    <section aria-label="Proposals map and list" className="wrap">
      <div style={{ paddingBottom: 20, display: "flex", flexWrap: "wrap", gap: "12px 24px", alignItems: "center", justifyContent: "space-between" }}>
        <AreaChips area={s.area} onChange={s.setArea} />
        <TopicSelect topic={s.topic} onChange={s.setTopic} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 440px", minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }} role="status" data-testid="results-count">
              {s.loading ? "Loading…" : `${s.items.length} proposal${s.items.length === 1 ? "" : "s"}`}{s.topicName && ` in ${s.topicName}`}{s.q && ` for “${s.q}”`}
            </p>
            <SortSeg sort={s.sort} onChange={s.setSort} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 720, overflowY: "auto", paddingRight: 4 }} data-testid="proposal-list">
            {!s.loading && !s.items.length && (
              <Empty title="No proposals match">
                Try another area, topic or search. <button type="button" className="btn-link" onClick={s.clear} data-testid="clear-filters-btn">Clear filters</button>
              </Empty>
            )}
            {s.items.map((p) => <ListCard key={p.id} p={p} selected={s.selected === p.id} onShow={s.show} />)}
          </div>
        </div>
        <div ref={mapBox} style={{ flex: "999 1 520px", position: "sticky", top: 16, height: 720, borderRadius: 18, overflow: "hidden", border: "1px solid var(--border)" }}>
          <ExploreMap proposals={s.items} selectedId={s.selected} onSelect={s.setSelected} flyTo={s.fly} topic={s.topic} onTopic={s.setTopic} />
        </div>
      </div>
    </section>
  </>
);

export default function Home() {
  const mobile = useIsMobile();
  const s = useHomeState();
  const top = useTopThree();
  const mapBox = useRef(null);

  useEffect(() => {
    if ((s.focus || s.topic) && mapBox.current) mapBox.current.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.focus]);

  if (mobile) return <MobileHome s={s} />;

  return (
    <div data-testid="home-page" style={{ position: "relative" }}>
      <SiteHeader transparent />
      <main>
        <Hero />
        <ExploreSection s={s} mapBox={mapBox} />
        {top.length > 0 ? <TopThree items={top} /> : <Loader />}
        <HowItWorks />
      </main>
      <SiteFooter />
    </div>
  );
}
